/**
 * @typedef {Object} StatusCountMap
 * @property {number} [status] - Count for each status (e.g., { "AC": 5, "CB": 3 })
 */

/**
 * @typedef {Object} CountChangesMap
 * @property {number} [status] - Change amount for each status (positive = increase, negative = decrease)
 * @example { "AC": -1, "CB": 1 } // Move 1 record from AC to CB
 */

/**
 * @typedef {Object} CallRecord
 * @property {string} _id - Unique identifier for the call record
 * @property {string} status - Current status abbreviation (e.g., "AC", "CB", "CL")
 * @property {number} occurrence_date - Unix timestamp of when the call occurred
 * @property {string} [recipientName] - Name of the recipient
 * @property {Object} [recipient] - Full recipient object
 * @property {string} [notes] - Call notes
 * @property {string} updatedAt - ISO timestamp of last update
 */

/**
 * @typedef {Object} BulkOperation
 * @property {'update'|'delete'} type - Type of bulk operation
 * @property {string} [newStatus] - New status for bulk updates (required for 'update' type)
 * @property {CallRecord[]} affectedRecords - Records that were affected by the operation
 */

/**
 * @typedef {Object} FetchResult
 * @property {CallRecord[]} data - Array of call records (filtered)
 * @property {CallRecord[]} [originalData] - Array of original call records (unfiltered, used for count calculations)
 * @property {Object} meta - Metadata about the fetch
 * @property {number} meta.total - Total number of records available
 * @property {number} meta.limit - Number of records fetched
 * @property {number} meta.skip - Number of records skipped
 * @property {boolean} meta.more - Whether more records are available
 */

/**
 * Handle full refresh result
 *
 * @param {FetchResult[]} results - Array of fetch result per status
 * @param {string[]} statuses - Array of status abbreviations (e.g., ["AC", "CB", "CL"])
 * @param {React.Dispatch<React.SetStateAction<StatusCountMap>>} setFetchCounts - React state setter for fetch counts
 * @param {React.Dispatch<React.SetStateAction<StatusCountMap>>} setTotalCounts - React state setter for total counts
 * @param {React.Dispatch<React.SetStateAction<CallRecord[]>>} setCommunications - React state setter for communications
 * @returns {string} Latest update timestamp
 **/
export const fullRefresh = (
  results,
  statuses,
  setFetchCounts,
  setTotalCounts,
  setCommunications
) => {
  const updatedTotalCounts = {};

  // Process the results data
  let latestUpdate = null;
  const newItems = [];

  // Collect all item IDs from results to determine which items to keep
  const resultItemIds = new Set();

  results.forEach(({ data, meta }, index) => {
    if (!data || !meta) {
      return;
    }
    updatedTotalCounts[statuses[index]] = meta.total;

    // Collect new items and track latest update
    if (data) {
      data.forEach((callTodo) => {
        newItems.push(callTodo);

        // Add this item's ID to our tracking set
        if (callTodo._id) {
          resultItemIds.add(callTodo._id);
        }

        // Update the latest timestamp if needed
        if (!latestUpdate || callTodo.updatedAt > latestUpdate) {
          latestUpdate = callTodo.updatedAt;
        }
      });
    }
  });

  // Initialize all statuses to 0, then apply the updated counts
  // This ensures statuses not in the results are reset to 0
  const resetCounts = {};
  statuses.forEach((status) => {
    resetCounts[status] = 0;
  });

  // Update state - no need for ...prev since fullRefresh replaces all counts
  setTotalCounts({ ...resetCounts, ...updatedTotalCounts });

  // Calculate fetch counts based on original (unfiltered) data to match totalCounts
  const fetchCounts = {};
  results.forEach(({ originalData, data }, index) => {
    const status = statuses[index];
    // Use filtered data for counting if available, otherwise fall back to originalData
    const dataForCounting = data || originalData || [];
    fetchCounts[status] = dataForCounting.length;
  });

  // Apply the same reset logic to fetchCounts to ensure consistency
  setFetchCounts({ ...resetCounts, ...fetchCounts });

  // Use functional update for communications to merge with existing state
  setCommunications((prevCommunications) => {
    // Filter out items that aren't in the results
    const filteredCommunications =
      prevCommunications?.filter((comm) => !comm._id || resultItemIds.has(comm._id)) || [];

    // Create a Map of filtered items for quick lookup
    const communicationsMap = new Map();
    filteredCommunications.forEach((comm) => {
      if (comm._id) {
        communicationsMap.set(comm._id, comm);
      }
    });

    // Update existing items and add new ones
    newItems.forEach((item) => {
      if (item._id) {
        communicationsMap.set(item._id, item);
      }
    });

    const newCommunications = Array.from(communicationsMap.values());

    return newCommunications.sort((a, b) => b.occurrence_date - a.occurrence_date);
  });

  return latestUpdate || new Date().toISOString();
};

/**
 * Handle incremental refresh result
 *
 * @param {FetchResult} result - Fetch result with new/updated records
 * @param {StatusCountMap} fetchCounts - Current fetch count per status
 * @param {StatusCountMap} totalCounts - Current total count per status
 * @param {React.Dispatch<React.SetStateAction<StatusCountMap>>} setFetchCounts - React state setter for fetch counts
 * @param {React.Dispatch<React.SetStateAction<StatusCountMap>>} setTotalCounts - React state setter for total counts
 * @param {CallRecord[]} communications - Array of existing call records
 * @param {React.Dispatch<React.SetStateAction<CallRecord[]>>} setCommunications - React state setter for communications
 * @returns {string} Latest update timestamp
 **/
export const incrementalRefresh = (
  result,
  fetchCounts,
  totalCounts,
  setFetchCounts,
  setTotalCounts,
  communications,
  setCommunications
) => {
  const updatedFetchCounts = { ...fetchCounts };
  const updatedTotalCounts = { ...totalCounts };

  // filter out updated rows from previous
  const updatedCommunications = communications.filter((comm) => {
    if ((result.data ?? []).some((newCom) => newCom._id === comm._id)) {
      updatedFetchCounts[comm.status]--;
      updatedTotalCounts[comm.status]--;
      return false;
    } else {
      return true;
    }
  });

  // append new or updated rows
  (result.data ?? []).forEach((newComm) => {
    if (newComm.status in updatedFetchCounts) {
      updatedFetchCounts[newComm.status]++;
      updatedTotalCounts[newComm.status]++;
    } else {
      updatedFetchCounts[newComm.status] = 1;
      updatedTotalCounts[newComm.status] = 1;
    }
    updatedCommunications.push(newComm);
  });

  // resort by occurrence date descending
  updatedCommunications.sort((a, b) => {
    return b.occurrence_date - a.occurrence_date;
  });

  setTotalCounts(updatedTotalCounts);
  setFetchCounts(updatedFetchCounts);
  setCommunications(updatedCommunications);

  const latestUpdate = updatedCommunications.reduce((latestUpdate, callTodo) => {
    if (!latestUpdate || callTodo.updatedAt > latestUpdate) {
      return callTodo.updatedAt;
    }
    return latestUpdate;
  }, null);
  return latestUpdate || new Date().toISOString();
};

/**
 * Handle load more result
 *
 * @param {FetchResult} result - Fetch result with additional records
 * @param {string} status - Status abbreviation to load more records for (e.g., "AC", "CB")
 * @param {StatusCountMap} fetchCounts - Current fetch count per status
 * @param {React.Dispatch<React.SetStateAction<StatusCountMap>>} setFetchCounts - React state setter for fetch counts
 * @param {CallRecord[]} communications - Array of existing call records
 * @param {React.Dispatch<React.SetStateAction<CallRecord[]>>} setCommunications - React state setter for communications
 **/
export const loadMore = (
  result,
  status,
  fetchCounts,
  setFetchCounts,
  communications,
  setCommunications
) => {
  // Process new data (filtered for display)
  const newData = result.data ?? [];
  // Use original data for counting if available, otherwise use filtered data
  const newDataForCounting = result.originalData ?? newData;

  // Update fetch counts using functional update based on original (unfiltered) data
  setFetchCounts((prevFetchCounts) => ({
    ...prevFetchCounts,
    [status]: (prevFetchCounts?.[status] ?? 0) + newDataForCounting.length,
  }));

  // Update communications using functional update
  setCommunications((prevCommunications) => {
    // Create a Map of existing communications for O(1) lookups
    const communicationsMap = new Map();
    prevCommunications.forEach((comm) => {
      if (comm._id) {
        communicationsMap.set(comm._id, comm);
      }
    });

    // Add or update communications from new data
    newData.forEach((item) => {
      if (item._id) {
        communicationsMap.set(item._id, item);
      }
    });

    // Convert Map values to array and sort
    const updatedCommunications = Array.from(communicationsMap.values());
    updatedCommunications.sort((a, b) => b.occurrence_date - a.occurrence_date);

    return updatedCommunications;
  });
};

/**
 * Helper function to apply count changes to both fetch and total counts
 * @param {CountChangesMap} countChanges - Object with status as key and change amount as value
 * @param {React.Dispatch<React.SetStateAction<StatusCountMap>>} setFetchCounts - React state setter for fetch counts
 * @param {React.Dispatch<React.SetStateAction<StatusCountMap>>} setTotalCounts - React state setter for total counts
 */
const applyCountChanges = (countChanges, setFetchCounts, setTotalCounts) => {
  setFetchCounts((prevFetchCounts) => {
    const updatedCounts = { ...prevFetchCounts };
    Object.keys(countChanges).forEach((status) => {
      updatedCounts[status] = Math.max(0, (updatedCounts[status] || 0) + countChanges[status]);
    });
    return updatedCounts;
  });

  setTotalCounts((prevTotalCounts) => {
    const updatedCounts = { ...prevTotalCounts };
    Object.keys(countChanges).forEach((status) => {
      updatedCounts[status] = Math.max(0, (updatedCounts[status] || 0) + countChanges[status]);
    });
    return updatedCounts;
  });
};

/**
 * Helper function to sort communications by occurrence date descending
 * @param {CallRecord[]} communications - Array of call records to sort
 * @returns {CallRecord[]} Sorted array of call records (newest first)
 */
const sortCommunications = (communications) => {
  return communications.sort((a, b) => b.occurrence_date - a.occurrence_date);
};

/**
 * Handle internal record updates without API calls
 * Updates counts when calls change status or are deleted
 *
 * @param {CallRecord|null} updatedCall - The updated call record (null if deleted)
 * @param {CallRecord} originalCall - The original call record before changes
 * @param {StatusCountMap} fetchCounts - Current fetch counts per status
 * @param {StatusCountMap} totalCounts - Current total counts per status
 * @param {React.Dispatch<React.SetStateAction<StatusCountMap>>} setFetchCounts - React state setter for fetch counts
 * @param {React.Dispatch<React.SetStateAction<StatusCountMap>>} setTotalCounts - React state setter for total counts
 * @param {CallRecord[]} communications - Array of call records
 * @param {React.Dispatch<React.SetStateAction<CallRecord[]>>} setCommunications - React state setter for communications
 **/
export const handleInternalUpdate = (
  updatedCall,
  originalCall,
  fetchCounts,
  totalCounts,
  setFetchCounts,
  setTotalCounts,
  communications,
  setCommunications
) => {
  // Handle call deletion
  if (!updatedCall && originalCall) {
    setCommunications((prevCommunications) => {
      const filteredCommunications = prevCommunications.filter(
        (comm) => comm._id !== originalCall._id
      );

      // Update counts for the deleted call
      applyCountChanges({ [originalCall.status]: -1 }, setFetchCounts, setTotalCounts);

      return filteredCommunications;
    });
    return;
  }

  // Handle status change
  if (updatedCall && originalCall && updatedCall.status !== originalCall.status) {
    setCommunications((prevCommunications) => {
      const updatedCommunications = prevCommunications.map((comm) =>
        comm._id === updatedCall._id ? updatedCall : comm
      );

      // Update counts - decrease old status, increase new status
      applyCountChanges(
        {
          [originalCall.status]: -1,
          [updatedCall.status]: 1,
        },
        setFetchCounts,
        setTotalCounts
      );

      return sortCommunications(updatedCommunications);
    });
  } else if (updatedCall) {
    // Just update the call without status change
    setCommunications((prevCommunications) =>
      prevCommunications.map((comm) => (comm._id === updatedCall._id ? updatedCall : comm))
    );
  }
};

/**
 * Handle bulk operations without API calls
 * Updates counts when multiple calls are updated or deleted
 *
 * @param {BulkOperation} bulkOperation - Bulk operation details
 * @param {StatusCountMap} fetchCounts - Current fetch counts per status
 * @param {StatusCountMap} totalCounts - Current total counts per status
 * @param {React.Dispatch<React.SetStateAction<StatusCountMap>>} setFetchCounts - React state setter for fetch counts
 * @param {React.Dispatch<React.SetStateAction<StatusCountMap>>} setTotalCounts - React state setter for total counts
 * @param {CallRecord[]} communications - Array of call records
 * @param {React.Dispatch<React.SetStateAction<CallRecord[]>>} setCommunications - React state setter for communications
 **/
export const handleBulkUpdate = (
  bulkOperation,
  fetchCounts,
  totalCounts,
  setFetchCounts,
  setTotalCounts,
  communications,
  setCommunications
) => {
  const { type, newStatus, affectedRecords } = bulkOperation;

  if (type === 'delete') {
    // Handle bulk deletion
    setCommunications((prevCommunications) => {
      const affectedIds = new Set(affectedRecords.map((record) => record._id));
      const filteredCommunications = prevCommunications.filter(
        (comm) => !affectedIds.has(comm._id)
      );

      // Calculate count changes for deleted calls
      const countChanges = {};
      affectedRecords.forEach((record) => {
        countChanges[record.status] = (countChanges[record.status] || 0) - 1;
      });

      // Apply count changes using shared helper
      applyCountChanges(countChanges, setFetchCounts, setTotalCounts);

      return filteredCommunications;
    });
  } else if (type === 'update') {
    // Handle bulk status update
    setCommunications((prevCommunications) => {
      const affectedIds = new Set(affectedRecords.map((record) => record._id));
      const updatedCommunications = prevCommunications.map((comm) => {
        if (affectedIds.has(comm._id)) {
          return { ...comm, status: newStatus };
        }
        return comm;
      });

      // Calculate count changes for status updates
      const countChanges = {};
      affectedRecords.forEach((record) => {
        if (record.status !== newStatus) {
          // Decrease old status count
          countChanges[record.status] = (countChanges[record.status] || 0) - 1;
          // Increase new status count
          countChanges[newStatus] = (countChanges[newStatus] || 0) + 1;
        }
      });

      // Apply count changes using shared helper
      applyCountChanges(countChanges, setFetchCounts, setTotalCounts);

      // Sort and return using shared helper
      return sortCommunications(updatedCommunications);
    });
  }
};
